import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createUser,
  deleteUser,
  getUsers,
  updateUserRoles,
} from "../../api/users";
import { toast } from "react-toastify";
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

const AVAILABLE_ROLES = [
  "super-admin",
  "car-admin",
  "image-admin",
  "user",
  "enquiry-admin",
];

// simple debounce hook
function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debounced;
}

// somewhere at the top of your file (or in a utils.js)
function formatRole(slug) {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function UsersAdmin() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [editingId, setEditingId] = useState(null);
  const [editedRoles, setEditedRoles] = useState([]);

  const [isAdding, setIsAdding] = useState(false);
  const [newUser, setNewUser] = useState({
    fullName: "",
    email: "",
    roles: [],
  });
  const [errors, setErrors] = useState({});

  // simple email check
  const emailRegex = /^\S+@\S+\.\S+$/;

  // validation + submit handler
  const handleSaveNewUser = () => {
    const errs = {};
    if (!newUser.fullName.trim()) errs.fullName = "Full Name is required";
    if (!newUser.email.trim()) errs.email = "Email is required";
    else if (!emailRegex.test(newUser.email))
      errs.email = "Invalid email format";
    if (newUser.roles.length === 0) errs.roles = "Pick at least one role";

    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    // clear errors & fire the mutation
    setErrors({});
    createUsr.mutate(newUser, {
      onSuccess: () => {
        qc.invalidateQueries(["adminUsers"]);
      },
    });
  };

  const qc = useQueryClient();

  // ─── Debounced queryKey & queryFn ───────────────────
  const {
    data: users = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["adminUsers", debouncedSearch],
    queryFn: () => getUsers(debouncedSearch.trim()),
    keepPreviousData: true,
  });

  // ─── Update Roles mutation ───────────────────────────
  const updRoles = useMutation({
    mutationFn: ({ userId, roles }) => updateUserRoles(userId, roles),
    onSuccess: () => {
      toast.success("Roles updated");
      setEditingId(null);
      // invalidate with the same key shape
      qc.invalidateQueries({ queryKey: ["adminUsers", debouncedSearch] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to update roles");
    },
  });

  // ─── Create User mutation ───────────────────────
  const createUsr = useMutation({
    mutationFn: createUser,
    onSuccess: (newUser) => {
      qc.invalidateQueries(["adminUsers", debouncedSearch]);
      toast.success("User created");
      setIsAdding(false);
      setNewUser({ fullName: "", email: "", roles: [] });
    },
  });

  // ─── Delete User mutation ───────────────────────
  const deleteUsr = useMutation({
    mutationFn: deleteUser,
    onSuccess: (_, deletedUserId) => {
      qc.setQueryData(["adminUsers", debouncedSearch], (old = []) =>
        old.filter((u) => u._id !== deletedUserId)
      );
      toast.success("User deleted");
    },
  });

  // if (isLoading) return <p>Loading users…</p>;
  if (isError) return <p className="text-red-600">Error loading users.</p>;

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4 text-white">User Management</h2>

      {/* ─── Search Bar ────────────────────────────────────────────── */}
      <input
        type="text"
        placeholder="Search by name or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-4 px-3 py-2 border rounded-lg w-72 text-primary"
      />

      <div className="flex items-start mb-4 space-x-4">
        {isAdding ? (
          <>
            {/* Full Name */}
            <div>
              <input
                type="text"
                placeholder="Full Name"
                value={newUser.fullName}
                onChange={(e) => {
                  setNewUser((u) => ({ ...u, fullName: e.target.value }));
                  setErrors((err) => ({ ...err, fullName: null }));
                }}
                className="px-3 py-2 border rounded"
              />
              {errors.fullName && (
                <div className="text-red-500 text-sm">{errors.fullName}</div>
              )}
            </div>

            {/* Email */}
            <div>
              <input
                type="email"
                placeholder="Email"
                value={newUser.email}
                onChange={(e) => {
                  setNewUser((u) => ({ ...u, email: e.target.value }));
                  setErrors((err) => ({ ...err, email: null }));
                }}
                className="px-3 py-2 border rounded"
              />
              {errors.email && (
                <div className="text-red-500 text-sm">{errors.email}</div>
              )}
            </div>

            {/* Roles */}
            <div>
              <div className="grid grid-cols-2 gap-2 border rounded p-2 max-h-32 overflow-y-auto">
                {AVAILABLE_ROLES.map((r) => (
                  <label key={r} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      value={r}
                      checked={newUser.roles.includes(r)}
                      onChange={(e) => {
                        const role = e.target.value;
                        setNewUser((u) => ({
                          ...u,
                          roles: e.target.checked
                            ? [...u.roles, role]
                            : u.roles.filter((x) => x !== role),
                        }));
                        setErrors((err) => ({ ...err, roles: null }));
                      }}
                    />
                    <span>{formatRole(r)}</span>
                  </label>
                ))}
              </div>
              {errors.roles && (
                <div className="text-red-500 text-sm">{errors.roles}</div>
              )}
            </div>

            {/* Buttons */}
            <button
              onClick={handleSaveNewUser}
              disabled={createUsr.isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded"
            >
              Save
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setNewUser({ fullName: "", email: "", roles: [] });
                setErrors({});
              }}
              className="px-4 py-2 bg-gray-400 text-white rounded"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsAdding(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            Add User
          </button>
        )}
      </div>

      {isLoading ? (
        <p>Loading users…</p>
      ) : (
        <div className="overflow-x-auto bg-surface rounded-lg shadow">
          <table className="w-full table-auto text-left">
            <thead className="bg-surface sticky top-0">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Roles</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u?._id} className="border-t">
                  <td className="px-4 py-2">{u?.fullName}</td>
                  <td className="px-4 py-2">{u?.email}</td>
                  <td className="px-4 py-2">
                    {editingId === u?._id ? (
                      <div className="grid grid-cols-2 gap-2">
                        {AVAILABLE_ROLES.map((r) => (
                          <label
                            key={r}
                            className="inline-flex items-center space-x-2"
                          >
                            <input
                              type="checkbox"
                              value={r}
                              checked={editedRoles.includes(r)}
                              onChange={(e) => {
                                const role = e.target.value;
                                setEditedRoles((prev) =>
                                  e.target.checked
                                    ? [...prev, role]
                                    : prev.filter((x) => x !== role)
                                );
                              }}
                            />
                            <span className="capitalize">
                              {r.replace("-", " ")}
                            </span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      u?.roles.map(formatRole).join(", ")
                    )}
                  </td>
                  <td className="px-4 py-2 space-x-2">
                    {editingId === u?._id ? (
                      <>
                        <button
                          onClick={() =>
                            updRoles.mutate({
                              userId: u._id,
                              roles: editedRoles,
                            })
                          }
                          disabled={updRoles.isLoading}
                          className="inline-flex items-center px-3 py-1 bg-primary text-white rounded hover:opacity-90"
                        >
                          <CheckIcon className="h-4 w-4 mr-1" />
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="inline-flex items-center px-3 py-1 bg-gray-400 rounded hover:opacity-90"
                        >
                          <XMarkIcon className="h-4 w-4 mr-1" />
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setEditingId(u?._id);
                            setEditedRoles(u?.roles);
                          }}
                          className="inline-flex items-center px-3 py-1 bg-accent text-white rounded hover:opacity-90"
                        >
                          <PencilIcon className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            if (
                              window.confirm(`Really delete ${u?.fullName}?`)
                            ) {
                              deleteUsr.mutate(u?._id);
                            }
                          }}
                          className="inline-flex items-center px-3 py-1 bg-red-600 text-white rounded hover:opacity-90"
                        >
                          {/* you can import TrashIcon from @heroicons/react/outline */}
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}

              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-white">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
